import type {
  PlatformBillingOrderDetail,
  PlatformBillingOrderPage,
  PlatformBillingOrderQuery,
  PlatformCreditPromotionView,
  PlatformCreditPromotionWrite,
  PlatformTenantBillingView,
  RefundRequest,
} from '../../../shared-types/src/index.js';
import { ADMIN_ROUTES } from '../api.js';
import type { AdminClientContext } from './client.js';

export interface PlatformBillingAdminMethods {
  getTenantBilling(tenantId: string): Promise<PlatformTenantBillingView>;
  setTenantBillingSettings(
    tenantId: string,
    input: { purchaseEnabled: boolean },
    idempotencyKey?: string
  ): Promise<PlatformTenantBillingView>;
  setTenantCreditPrice(
    tenantId: string,
    productId: string,
    input: { amountKrw: number | null },
    idempotencyKey?: string
  ): Promise<PlatformTenantBillingView>;
  createCreditPromotion(
    tenantId: string,
    input: PlatformCreditPromotionWrite,
    idempotencyKey?: string
  ): Promise<PlatformCreditPromotionView>;
  updateCreditPromotion(
    tenantId: string,
    promotionId: string,
    input: PlatformCreditPromotionWrite,
    idempotencyKey?: string
  ): Promise<PlatformCreditPromotionView>;
  deleteCreditPromotion(
    tenantId: string,
    promotionId: string,
    idempotencyKey?: string
  ): Promise<void>;
  listBillingOrders(query: PlatformBillingOrderQuery): Promise<PlatformBillingOrderPage>;
  getBillingOrder(orderId: string): Promise<PlatformBillingOrderDetail>;
  refundBillingOrder(
    orderId: string,
    input: RefundRequest,
    idempotencyKey?: string
  ): Promise<PlatformBillingOrderDetail>;
}

function route(path: string, params: Record<string, string>): string {
  return Object.entries(params).reduce(
    (resolved, [key, value]) => resolved.replace(`:${key}`, encodeURIComponent(value)),
    path
  );
}

function withQuery(path: string, query: PlatformBillingOrderQuery): string {
  const search = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.tenantId) search.set('tenantId', query.tenantId);
  if (query.status) search.set('status', query.status);
  return `${path}?${search.toString()}`;
}

export function buildBillingMethods(ctx: AdminClientContext): PlatformBillingAdminMethods {
  const { request, token, idemKey } = ctx;

  return {
    getTenantBilling: (tenantId) =>
      request(
        'GET',
        route(ADMIN_ROUTES.platformBillingTenantSettingsGet.path, { tenantId }),
        token()
      ),
    setTenantBillingSettings: (tenantId, input, idempotencyKey) =>
      request(
        'PATCH',
        route(ADMIN_ROUTES.platformBillingTenantSettingsPatch.path, { tenantId }),
        token(),
        input,
        idempotencyKey ?? idemKey()
      ),
    setTenantCreditPrice: (tenantId, productId, input, idempotencyKey) =>
      request(
        'PUT',
        route(ADMIN_ROUTES.platformBillingTenantProductPricePut.path, {
          tenantId,
          productId,
        }),
        token(),
        input,
        idempotencyKey ?? idemKey()
      ),
    createCreditPromotion: (tenantId, input, idempotencyKey) =>
      request(
        'POST',
        route(ADMIN_ROUTES.platformBillingTenantPromotionsCreate.path, {
          tenantId,
        }),
        token(),
        input,
        idempotencyKey ?? idemKey()
      ),
    updateCreditPromotion: (tenantId, promotionId, input, idempotencyKey) =>
      request(
        'PATCH',
        route(ADMIN_ROUTES.platformBillingTenantPromotionPatch.path, {
          tenantId,
          promotionId,
        }),
        token(),
        input,
        idempotencyKey ?? idemKey()
      ),
    deleteCreditPromotion: async (tenantId, promotionId, idempotencyKey) => {
      await request(
        'DELETE',
        route(ADMIN_ROUTES.platformBillingTenantPromotionDelete.path, {
          tenantId,
          promotionId,
        }),
        token(),
        undefined,
        idempotencyKey ?? idemKey()
      );
    },
    listBillingOrders: (query) =>
      request('GET', withQuery(ADMIN_ROUTES.platformBillingOrdersList.path, query), token()),
    getBillingOrder: (orderId) =>
      request('GET', route(ADMIN_ROUTES.platformBillingOrderGet.path, { orderId }), token()),
    refundBillingOrder: (orderId, input, idempotencyKey) => {
      const key = idempotencyKey || input.idempotencyKey || idemKey();
      return request(
        'POST',
        route(ADMIN_ROUTES.platformBillingOrderRefundCreate.path, { orderId }),
        token(),
        { ...input, idempotencyKey: key },
        key
      );
    },
  };
}
